// The capability catalogue, mirroring the backend's `Permission` enum. Endpoints
// require permissions and roles are only collections of them, so every gate in
// this app asks about a permission — never about a role.
//
// `Permission` is the generated union itself, imported as a TYPE ONLY: importing
// the generated enum's runtime value would make every gate depend on the
// generated module being loaded, which any test that mocks it would break. The
// literals below are held to that union by `satisfies`, and the scope map keyed
// by it — so a permission added or renamed on the backend is a compile error
// here rather than a gate that silently never matches.

import type { CurrentLocationPermissionsResponsePermissionsEnum } from '../api/generated';

// Both generated permission enums (account and location) are the same union, so
// one type covers both responses.
export type Permission = CurrentLocationPermissionsResponsePermissionsEnum;

export const PERMISSIONS = {
  // Location permissions: held through a role at one centre.
  YearPlanRead: 'YEAR_PLAN_READ',
  YearPlanWrite: 'YEAR_PLAN_WRITE',
  AlgorithmJobsExecute: 'ALGORITHM_JOBS_EXECUTE',
  PatientRead: 'PATIENT_READ',
  PatientWrite: 'PATIENT_WRITE',
  PatientAssign: 'PATIENT_ASSIGN',
  BookingRead: 'BOOKING_READ',
  BookingWrite: 'BOOKING_WRITE',
  EconomicsRead: 'ECONOMICS_READ',
  EconomicsWrite: 'ECONOMICS_WRITE',
  StaffRead: 'STAFF_READ',
  StaffWrite: 'STAFF_WRITE',
  InterventionRoomsRead: 'INTERVENTION_ROOMS_READ',
  InterventionRoomsWrite: 'INTERVENTION_ROOMS_WRITE',
  PatientRoomsRead: 'PATIENT_ROOMS_READ',
  PatientRoomsWrite: 'PATIENT_ROOMS_WRITE',
  // Account permissions: tenant-wide, held by the account administrator.
  TreatmentPlanTemplatesRead: 'TREATMENT_PLAN_TEMPLATES_READ',
  TreatmentPlanTemplatesWrite: 'TREATMENT_PLAN_TEMPLATES_WRITE',
  LocationAdmin: 'LOCATION_ADMIN',
  StaffProfessionRead: 'STAFF_PROFESSION_READ',
  StaffProfessionWrite: 'STAFF_PROFESSION_WRITE',
  InterventionRoomPropertiesRead: 'INTERVENTION_ROOM_PROPERTIES_READ',
  InterventionRoomPropertiesWrite: 'INTERVENTION_ROOM_PROPERTIES_WRITE',
  PatientRoomPropertiesRead: 'PATIENT_ROOM_PROPERTIES_READ',
  PatientRoomPropertiesWrite: 'PATIENT_ROOM_PROPERTIES_WRITE',
  UserAdmin: 'USER_ADMIN'
} as const satisfies Record<string, Permission>;

// Which resolution rule applies, exactly as on the backend: a LOCATION
// permission is held at one location at a time, an ACCOUNT one is tenant-wide.
// Kept next to the catalogue so a gate cannot check an account permission
// against a location, or the other way round.
export const PERMISSION_SCOPES = {
  location: 'location',
  account: 'account'
} as const;

export type PermissionScope = (typeof PERMISSION_SCOPES)[keyof typeof PERMISSION_SCOPES];

// Keyed by the generated union rather than by the literals above, which is what
// makes a newly added backend permission fail to compile until it is placed in a
// scope.
export const PERMISSION_SCOPE_BY_PERMISSION: Record<Permission, PermissionScope> = {
  [PERMISSIONS.YearPlanRead]: PERMISSION_SCOPES.location,
  [PERMISSIONS.YearPlanWrite]: PERMISSION_SCOPES.location,
  [PERMISSIONS.AlgorithmJobsExecute]: PERMISSION_SCOPES.location,
  [PERMISSIONS.PatientRead]: PERMISSION_SCOPES.location,
  [PERMISSIONS.PatientWrite]: PERMISSION_SCOPES.location,
  [PERMISSIONS.PatientAssign]: PERMISSION_SCOPES.location,
  [PERMISSIONS.BookingRead]: PERMISSION_SCOPES.location,
  [PERMISSIONS.BookingWrite]: PERMISSION_SCOPES.location,
  [PERMISSIONS.EconomicsRead]: PERMISSION_SCOPES.location,
  [PERMISSIONS.EconomicsWrite]: PERMISSION_SCOPES.location,
  [PERMISSIONS.StaffRead]: PERMISSION_SCOPES.location,
  [PERMISSIONS.StaffWrite]: PERMISSION_SCOPES.location,
  [PERMISSIONS.InterventionRoomsRead]: PERMISSION_SCOPES.location,
  [PERMISSIONS.InterventionRoomsWrite]: PERMISSION_SCOPES.location,
  [PERMISSIONS.PatientRoomsRead]: PERMISSION_SCOPES.location,
  [PERMISSIONS.PatientRoomsWrite]: PERMISSION_SCOPES.location,
  [PERMISSIONS.TreatmentPlanTemplatesRead]: PERMISSION_SCOPES.account,
  [PERMISSIONS.TreatmentPlanTemplatesWrite]: PERMISSION_SCOPES.account,
  [PERMISSIONS.LocationAdmin]: PERMISSION_SCOPES.account,
  [PERMISSIONS.StaffProfessionRead]: PERMISSION_SCOPES.account,
  [PERMISSIONS.StaffProfessionWrite]: PERMISSION_SCOPES.account,
  [PERMISSIONS.InterventionRoomPropertiesRead]: PERMISSION_SCOPES.account,
  [PERMISSIONS.InterventionRoomPropertiesWrite]: PERMISSION_SCOPES.account,
  [PERMISSIONS.PatientRoomPropertiesRead]: PERMISSION_SCOPES.account,
  [PERMISSIONS.PatientRoomPropertiesWrite]: PERMISSION_SCOPES.account,
  [PERMISSIONS.UserAdmin]: PERMISSION_SCOPES.account
};

export const getPermissionScope = (permission: Permission): PermissionScope =>
  PERMISSION_SCOPE_BY_PERMISSION[permission];
